import React, { useMemo, useState, MouseEvent } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { AutoComplete, Button } from "../index";
import { AddUserIcon, RemoveUserIcon, ArrowDown } from "../Icons";

import * as api from "./modules/api";
import styles from "./AssigneeSelector.module.scss";

interface Props {
  entityId: string;
  isAssignable: boolean;
  isUnAssignable?: boolean;
  isAssigned: boolean;
  currentUserId: number;
  assigneeId?: string | number;
  assigneeName?: string;
  className?: string;
  filteringRoles?: string[];
  loadOnlyActive?: boolean;
  onChange: (assigneeId: string | number | null) => void;
}

const AssigneeSelector = ({
  entityId,
  isAssignable,
  currentUserId,
  isAssigned,
  assigneeId,
  assigneeName,
  onChange,
  filteringRoles,
  className,
  isUnAssignable = true,
  loadOnlyActive
}: Props) => {
  const [isSelectorOpened, openSelector] = useState(false);
  const { t } = useTranslation();

  const generateRolesQueryKey = (filteringRoles: string[]) => filteringRoles.toString();

  const queryKey = filteringRoles ? generateRolesQueryKey(filteringRoles) : "allInternalUsers";

  const getInternalUsersApiRequest = filteringRoles ?
    () => api.getInternalUsersByRoles({
      IsActive: loadOnlyActive,
      roleCodes: filteringRoles
    })
    :
    () => api.getInternalUsers({
      IsActive: loadOnlyActive
    });

  const { data: internalUsers = [] } = useQuery(
    queryKey,
    getInternalUsersApiRequest,
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const selectOptions = useMemo(() => {
    const unAssignOption = {
      value: api.UNASSIGN_VALUE,
      label: t("grid.unassign"),
      iconComponent: RemoveUserIcon,
    };
    const mappedInternalUsers = internalUsers
      .filter((u) => u.id !== assigneeId)
      .map((u) => ({
        value: u.id,
        label: `${u.displayName}${u.id === currentUserId ? ` (${t("grid.you")})` : ""
          }`,
      }));

    if (isAssigned && isUnAssignable) {
      mappedInternalUsers.unshift(unAssignOption);
    }

    const currentUserIndex = mappedInternalUsers.findIndex(
      (i) => i.value === currentUserId
    );

    if (currentUserIndex !== -1) {
      const [currentUser] = mappedInternalUsers.splice(currentUserIndex, 1);

      mappedInternalUsers.unshift(currentUser);
    }

    return mappedInternalUsers;
  }, [assigneeId, currentUserId, internalUsers, isAssigned, isUnAssignable, t]);

  const changeSelectorState = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openSelector(!isSelectorOpened);
  };

  const onAssigneeChange = (value: string | number | null) => {
    if (value) {
      openSelector(false);
      onChange(value);
    }
  };

  return (
    <div className={className}>
      {isSelectorOpened ? (
        <AutoComplete
          isAutoFocused
          options={selectOptions}
          placeholder={t("grid.search")}
          id={`autocomplete-${entityId}`}
          onBlur={() => openSelector(false)}
          onChange={(e, value) => onAssigneeChange(value)}
        />
      ) : (
        <div>
          {isAssignable && !isAssigned && (
            <Button
              variant="outlined"
              iconComponent={AddUserIcon}
              onClick={changeSelectorState}
              id={`assign-button-${entityId}`}
              className={styles.assigneeAddButton}
            >
              {t("grid.assign")}
            </Button>
          )}
          {!isAssignable && isAssigned && <span>{assigneeName}</span>}
          {isAssignable && isAssigned && (
            <Button
              variant="outlined"
              iconPosition="right"
              iconComponent={ArrowDown}
              onClick={changeSelectorState}
              className={styles.reAssignButton}
              id={`reassign-button-${entityId}`}
            >
              <span>{assigneeName}</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AssigneeSelector;
